import { useEffect, useState } from 'react';
import { ApiHelper } from '../apiHelper/ApiHelper';
import { readJson } from '../apiHelper/readJson';
import { SECTION_TILES, useSections, Section } from '../sections';
import { Faculty, loadFaculties, getCachedFaculties } from '../facultiesCache';
import './styles/OnboardingWizard.scss';

// Tailored "how to use it" line per section. The wizard only shows the ones the
// user's product actually unlocks (driven by useSections), so the intro matches
// what they bought instead of advertising features they can't open.
const FEATURE_DETAIL: Partial<Record<Section, string>> = {
    StudyMaterials: "Procházej texty, videa a dokumenty ke každému tématu vlastním tempem.",
    OnlineLessons: "Najdeš tu rozvrh živých online lekcí s lektorem i jejich záznamy.",
    Practice: "Ověř si znalosti kvízy a modelovými testy na míru své fakultě.",
    AiTutor: "Když ti něco nejde, AI tutor ti to vysvětlí krok za krokem.",
    Community: "Zapoj se do diskuzních kanálů svého ročníku — ptej se a sdílej.",
};

const formatDate = (d: string) => {
    const date = new Date(d);
    return date.getDate() + ". " + (date.getMonth() + 1) + ". " + date.getFullYear();
};

// Introductory wizard shown on first entry to the learning space (and replayable
// from the account page). Explains the platform according to the bought product
// and collects the user's exam goal. Reuses SetGoal + the existing faculties API.
export default function OnboardingWizard({ onClose }: { onClose: () => void }) {
    const { hasSection, loading: sectionsLoading } = useSections();
    const [step, setStep] = useState(0);
    const [faculties, setFaculties] = useState<Faculty[]>(getCachedFaculties() ?? []);
    const [facultyId, setFacultyId] = useState("");
    const [saving, setSaving] = useState(false);

    const api = new ApiHelper();

    const features = SECTION_TILES.filter((t) => hasSection(t.section) && FEATURE_DETAIL[t.section]);
    const steps = ["welcome", "features", "goal"];
    const isLast = step === steps.length - 1;

    async function load() {
        try {
            setFaculties(await loadFaculties(api));

            const goalRes = await api.userApi.getGoalList();
            const goal = await readJson(goalRes);
            if (goal) setFacultyId(goal.id);
        } catch (x: any) {
            api.processErrors(x);
        }
    }

    useEffect(() => { load(); }, []);

    async function finish() {
        setSaving(true);
        try {
            if (facultyId) await api.userApi.setGoalCreate({ facultyId: facultyId });
            await api.userApi.completeOnboardingCreate();
            onClose();
        } catch (x: any) {
            api.processErrors(x);
            setSaving(false);
        }
    }

    // Skip still marks onboarding done so it doesn't nag on every visit.
    async function skip() {
        setSaving(true);
        try {
            await api.userApi.completeOnboardingCreate();
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            onClose();
        }
    }

    const selectedExamDate = faculties.find((f) => f.id === facultyId)?.examDate ?? null;
    const examDays = selectedExamDate
        ? Math.ceil((new Date(selectedExamDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        : null;

    return (
        <div className="onboardingOverlay">
            <div className="onboardingBox">
                <div className="obHead">
                    <div className="obDots">
                        {steps.map((_, i) => (
                            <span key={i} className={i === step ? "dot active" : "dot"} />
                        ))}
                    </div>
                    <div className="obSkip" onClick={skip}>Přeskočit</div>
                </div>

                {step === 0 &&
                    <div className="obStep">
                        <div className="obIcon">👋</div>
                        <h2>Vítej v MyMed!</h2>
                        <p>
                            Jsme rádi, že tu jsi. Tahle krátká nástěnka tě ve třech krocích provede
                            tím, co máš ve svém kurzu k dispozici a jak se v tom vyznat.
                        </p>
                        <p className="obSub">Zabere to necelou minutu.</p>
                    </div>
                }

                {step === 1 &&
                    <div className="obStep">
                        <h2>Co máš k dispozici</h2>
                        <p className="obSub">Podle tvého produktu máš odemčené tyto části:</p>
                        {sectionsLoading
                            ? <p className="obSub">Načítání…</p>
                            : <div className="obFeatures">
                                {features.map((f) => (
                                    <div key={f.section} className="obFeature">
                                        <span className="obCheck">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        </span>
                                        <div className="obFeatureText">
                                            <h3>{f.title}</h3>
                                            <span>{FEATURE_DETAIL[f.section]}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                }

                {step === 2 &&
                    <div className="obStep">
                        <h2>Jaký je tvůj cíl?</h2>
                        <p className="obSub">
                            Vyber fakultu, na kterou míříš. Podle ní ti přizpůsobíme procvičování a
                            modelové testy. Změnit to můžeš kdykoliv v účtu.
                        </p>
                        <div className="obGoal">
                            <label>Cílová fakulta</label>
                            <select value={facultyId} onChange={(e) => setFacultyId(e.currentTarget.value)}>
                                <option value="">-- vyber fakultu --</option>
                                {faculties.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        {facultyId &&
                            <p className="obExam">
                                {selectedExamDate
                                    ? <>Termín přijímaček: <b>{formatDate(selectedExamDate)}</b>{examDays !== null && examDays >= 0 && <> — zbývá <b>{examDays} dní</b></>}</>
                                    : <>Termín přijímaček zatím nebyl zveřejněn.</>
                                }
                            </p>
                        }
                    </div>
                }

                <div className="obActions">
                    {step > 0
                        ? <div className="obBtn ghost" onClick={() => setStep(step - 1)}>Zpět</div>
                        : <span />
                    }
                    {isLast
                        ? <div className={saving ? "obBtn primary disabled" : "obBtn primary"} onClick={() => !saving && finish()}>Hotovo, jdeme na to</div>
                        : <div className="obBtn primary" onClick={() => setStep(step + 1)}>Pokračovat</div>
                    }
                </div>
            </div>
        </div>
    );
}
